<template>
  <div ref="barChartEl" style="height: 400px"></div>
</template>

<script setup lang="ts">
import { useI18n } from "@/composables/useI18n";
import { formatCurrency } from "@/utils/tools";
import { BarChart } from "echarts/charts";
import {
  DatasetComponent,
  GridComponent,
  LegendComponent,
  TooltipComponent,
} from "echarts/components";
import * as echarts from "echarts/core";
import { SVGRenderer } from "echarts/renderers";
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useTheme } from "vuetify";

echarts.use([
  BarChart,
  DatasetComponent,
  TooltipComponent,
  GridComponent,
  LegendComponent,
  SVGRenderer,
]);

const props = defineProps<{
  data: {
    positive: number;
    negative: number;
    date: string;
  }[];
  currencyCode: string;
}>();

const barChartEl = ref<HTMLDivElement | null>(null);

let chart: echarts.ECharts | null = null;
let resizeObserver: ResizeObserver | null = null;

const { t, locale } = useI18n();
const theme = useTheme();
const isDark = computed(() => theme.current.value.dark);

function parseDate(input: string): Date {
  const m = /^(\d{4})-(\d{2})(?:-(\d{2}))?$/.exec(input);
  if (m && m[1] !== undefined && m[2] !== undefined) {
    const y = parseInt(m[1], 10);
    const month = parseInt(m[2], 10) - 1;
    const d = m[3] ? parseInt(m[3], 10) : 1;
    return new Date(y, month, d);
  }
  return new Date(input);
}

function formatMonthLabel(input: string): string {
  const dt = parseDate(input);
  return Number.isFinite(dt.getTime())
    ? dt.toLocaleDateString(undefined, { month: "short", year: "2-digit" })
    : input;
}

onMounted(() => {
  const el = barChartEl.value;
  if (!el) return;
  chart = echarts.init(el);
  resizeObserver = new ResizeObserver(() => {
    chart?.resize();
  });
  resizeObserver.observe(el);
  setOption(props.data);
});

onBeforeUnmount(() => {
  resizeObserver?.disconnect();
  resizeObserver = null;
  chart?.dispose();
  chart = null;
});

watch(
  () => props.data,
  (value) => {
    setOption(value);
  },
);

watch([locale, isDark], () => {
  setOption(props.data);
});

type Data = {
  positive: number;
  negative: number;
  date: string;
};

const setOption = (data: Data[]) => {
  const textColor = isDark.value ? "rgba(255, 255, 255, 0.87)" : "rgba(0, 0, 0, 0.87)";
  const mutedColor = isDark.value ? "rgba(255, 255, 255, 0.6)" : "rgba(0, 0, 0, 0.6)";
  const option = {
    textStyle: { color: textColor },
    grid: { bottom: "15%", top: "15%", left: "10%", right: "10%" },
    legend: { top: "top", textStyle: { color: textColor } },
    tooltip: {
      trigger: "item",
      valueFormatter: (value: number) =>
        formatCurrency({ value, currencyCode: props.currencyCode, locale: locale.value }),
    },
    dataset: {
      source: [...data]
        .sort((a, b) => parseDate(a.date).getTime() - parseDate(b.date).getTime())
        .slice(Math.max(data.length - 12, 0))
        .map((e) => ({
          date: formatMonthLabel(e.date),
          positive: e.positive,
          negative: Math.abs(e.negative),
        })),
    },
    xAxis: {
      type: "category",
      axisLabel: { color: mutedColor },
      axisLine: { lineStyle: { color: mutedColor } },
    },
    yAxis: {
      axisLabel: { color: mutedColor },
      splitLine: {
        lineStyle: { color: isDark.value ? "rgba(255, 255, 255, 0.12)" : "rgba(0, 0, 0, 0.12)" },
      },
    },
    series: [
      {
        name: t("barChart.inflows"),
        type: "bar",
        color: "#00C5D7",
        label: {
          show: true,
          position: "top",
          color: textColor,
          formatter: (value: { data: Data }) => {
            return formatCurrency({
              value: value.data.positive,
              currencyCode: props.currencyCode,
              noDigits: true,
              locale: locale.value,
            });
          },
        },
      },
      {
        name: t("barChart.outflows"),
        type: "bar",
        color: "#D5464D",
        label: {
          show: true,
          position: "top",
          color: textColor,
          formatter: (value: { data: Data }) => {
            return formatCurrency({
              value: value.data.negative,
              currencyCode: props.currencyCode,
              noDigits: true,
              locale: locale.value,
            });
          },
        },
      },
    ],
  };

  chart?.setOption(option, { notMerge: true });
};
</script>
